import { useState } from "react";
import { Link, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { Store, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError, api } from "@/lib/api";
import { AuthLayout, GoogleButton, destinationFor } from "@/pages/Login";
import { OnboardingExit } from "@/components/OnboardingExit";
import { CONSENT_ITEMS, EMPTY_CONSENT } from "@/constants/legalContent";

export const ConsentBlock = ({ value, onChange, testIdPrefix = "consent" }) => (
  <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`${testIdPrefix}-block`}>
    <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Required acknowledgements</p>
    {CONSENT_ITEMS.map((item) => (
      <label key={item.key} className="flex cursor-pointer items-start gap-2.5 text-sm leading-relaxed text-slate-700">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 shrink-0 accent-[#1E3A8A]"
          checked={!!value[item.key]}
          onChange={(e) => onChange({ ...value, [item.key]: e.target.checked })}
          data-testid={`${testIdPrefix}-${item.key}`}
        />
        <span>
          {item.label}{" "}
          {item.link && (
            <Link to={item.link} target="_blank" className="font-medium text-brand hover:underline">
              Read it
            </Link>
          )}
        </span>
      </label>
    ))}
  </div>
);

export const allConsented = (value) => CONSENT_ITEMS.every((i) => value[i.key]);

const RoleToggle = ({ value, onChange }) => (
  <div>
    <p className="field-label">I am signing up as</p>
    <div className="grid grid-cols-2 gap-3">
      {[
        { key: "pharmacy", label: "A pharmacy", hint: "I need staff", icon: Store },
        { key: "worker", label: "A pharmacy worker", hint: "I want to work shifts", icon: UserRound },
      ].map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          data-testid={`role-${o.key}`}
          className={`flex flex-col items-start gap-1.5 rounded-xl border p-4 text-left transition-all ${
            value === o.key ? "border-brand bg-brand-soft/50 shadow-sm" : "border-slate-200 hover:border-brand/40"
          }`}
        >
          <o.icon className={`h-5 w-5 ${value === o.key ? "text-brand" : "text-slate-400"}`} />
          <span className="text-sm font-medium text-slate-800">{o.label}</span>
          <span className="text-xs text-slate-500">{o.hint}</span>
        </button>
      ))}
    </div>
    {!value && (
      <p className="mt-2 text-xs text-slate-500" data-testid="role-required-hint">
        Choose one to continue — pharmacies verify an organisation NPI, workers pick their role next.
      </p>
    )}
  </div>
);

export default function Register() {
  const [params] = useSearchParams();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(
    ["worker", "pharmacy"].includes(params.get("role")) ? params.get("role") : "");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [consent, setConsent] = useState(EMPTY_CONSENT);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await register({ ...form, ...consent, role });
      navigate(destinationFor(user));
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Create your account" subtitle="Two minutes to your first shift. No credit card, no contracts.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <RoleToggle value={role} onChange={setRole} />
        <div>
          <label className="field-label">{role === "pharmacy" ? "Contact person" : "Full name"}</label>          <input
            className="field-input"
            required
            data-testid="register-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div>
          <label className="field-label">Email</label>
          <input
            className="field-input"
            type="email"
            required
            data-testid="register-email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div>
          <label className="field-label">Phone</label>
          <input
            className="field-input"
            data-testid="register-phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>
        <div>
          <label className="field-label">Password</label>
          <input
            className="field-input"
            type="password"
            minLength={6}
            required
            data-testid="register-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </div>
        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="register-error">
            {error}
          </p>
        )}
        <ConsentBlock value={consent} onChange={setConsent} />
        <button
          type="submit"
          disabled={busy || !role || !allConsented(consent)}
          className="btn-primary w-full"
          data-testid="register-submit"
        >
          {busy ? "Creating…" : "Create account"}
        </button>
        {!allConsented(consent) && (
          <p className="text-xs text-slate-500" data-testid="consent-required-hint">
            Tick every acknowledgement above to enable account creation.
          </p>
        )}
      </form>

      <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400">
        <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
      </div>
      <GoogleButton label="Sign up with Google" />

      <p className="mt-6 text-sm text-slate-600">
        Already have an account?{" "}
        <Link to="/login" className="font-medium text-brand" data-testid="register-to-login">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}

export function ChooseRole() {
  const { user, refresh, loading } = useAuth();
  const navigate = useNavigate();
  const [consent, setConsent] = useState(EMPTY_CONSENT);
  const [error, setError] = useState("");
  const needsConsent = user && !user.has_consent;

  if (loading || user === null) return null;
  if (!user) return <Navigate to="/login" replace />;

  const pick = async (role) => {
    setError("");
    try {
      if (needsConsent) await api.post("/consents", consent);
      if (!user?.role) await api.post("/auth/role", { role });
      const updated = await refresh();
      navigate(destinationFor(updated));
    } catch (e) {
      setError(apiError(e));
    }
  };

  const ready = !needsConsent || allConsented(consent);

  return (
    <AuthLayout
      title={user.role ? "One last step" : "How will you use FindRxStaff?"}
      subtitle={user.role
        ? "Accept the agreements below to activate your account."
        : "Pick the account type that fits you."}
    >
      <div className="space-y-4" data-testid="choose-role">
        <OnboardingExit backTo="/login" backLabel="Back to sign in" />
        {needsConsent && <ConsentBlock value={consent} onChange={setConsent} testIdPrefix="google-consent" />}
        {user.role ? (
          <button onClick={() => pick(user.role)} disabled={!ready} className="btn-primary w-full" data-testid="choose-continue">
            Continue
          </button>
        ) : (
          <>
            <button onClick={() => pick("pharmacy")} disabled={!ready} className="btn-primary w-full" data-testid="choose-pharmacy">
              I need staff — I run a pharmacy
            </button>
            <button onClick={() => pick("worker")} disabled={!ready} className="btn-accent w-full" data-testid="choose-worker">
              I want to work shifts
            </button>
          </>
        )}
        {error && <p className="text-sm text-red-700" data-testid="choose-role-error">{error}</p>}
      </div>
    </AuthLayout>
  );
}
